
import { API_TTaskSelect, TTaskSelect } from "@/models/expert_tasks";
import { SMAdditionalInfo, SMAlert, SMCases, SMDocuments, SMUser } from "@/store";
import { API_ROUTES } from "@/utils/API";
import { format } from "date-fns";
import { Component, Vue, Watch } from "vue-property-decorator";

import {
  APPROVED_BY_HO_LABEL,
  APPROVED_BY_HO_OA_STATUS,
  APPROVED_SERVICE_STATUS,
  CANDIDATE_ASSESSMENT_SERVICE_TYPE,
  COS_CATEGORIES,
  COS_TAB_NAME,
  REJECTED_BY_HO_LABEL,
  REJECTED_BY_HO_OA_STATUS,
  SKILLED_WORKER_COS_INSIDE_SERVICE_TYPE,
  SKILLED_WORKER_COS_SERVICE_TYPE,
  SKILLED_WORKER_INSIDE_SERVICE_TYPE,
  SKILLED_WORKER_SERVICE_TYPE,
  SPONSOR_LICENCE_SERVICE_TYPE,
  TAB_NAMES,
  TO_REVIEW_SERVICE_STATUS,
  WITHDRAW_APPROVAL_LABEL,
  WITHDRAW_REJECTION_LABEL,
} from "@/constants/services";

import { ASK_POPUP_TYPE, WARNING_POPUP_TYPE } from "@/constants/popupTypes";

import { ERROR_DOCUMENT_STATUS, NOT_PROVIDED_DOCUMENT_STATUS } from "@/constants/documents";

const ROUTE_LABEL_DOCUMENT = "documents";
const ROUTE_NAME_OA = "online_application";

const GET_REQUEST_TYPE = "get";
const BOT_HTTP_REQUEST_TIMEOUT = 10 * 60 * 1000;
const HO_AGENT_API_BASE = "https://ho-agent.nationbetter.uk/api/v1";

type HoAgentBotType = "cos" | "inside";

type FormbotGap = {
  key: string;
  element?: string;
  page?: string;
  problem: "missing" | "conditional" | "malformed" | "rejected" | "unsupported";
  because?: "missing" | "malformed";
  detail?: string;
  // The platform's own question and the profile category it is answered in, set by
  // the API when a profile row carries the key. The key is the fallback.
  question?: string;
  section?: string;
};

type FormbotNote = {
  key?: string;
  source?: string;
  why?: string;
};

// The case's latest run, as the platform recorded it (null: never run).
type FormbotLastRun = {
  status: "running" | "done" | "needs_human" | "failed" | "lost";
  code?: string;
  started_at: string;
  finished_at?: string;
  expert?: string;
  page?: string;
  explanation?: string;
  gaps?: string[] | null;
  // The run stopped and the case would send the bot exactly the same thing again.
  same_input?: boolean;
};

type FormbotPlanResponse = {
  notes?: FormbotNote[] | null;
  warnings?: FormbotGap[] | null;
  last_run?: FormbotLastRun | null;
  plan?: {
    missing?: FormbotGap[] | null;
    malformed?: FormbotGap[] | null;
    conditional?: FormbotGap[] | null;
    unsupported?: FormbotGap[] | null;
  };
};

type FormbotRunResponse = {
  status?: string;
  case_id?: number;
  notify?: string;
  error?: string;
  warnings?: FormbotGap[] | null;
  missing?: FormbotGap[] | null;
  notes?: FormbotNote[] | null;
  last_run?: FormbotLastRun | null;
  message?: string;
};

const asFormbotList = (value: FormbotGap[] | null | undefined): FormbotGap[] => value ?? [];

const asFormbotNoteList = (value: FormbotNote[] | null | undefined): FormbotNote[] => value ?? [];

// "trips.2.reason" -> "trips #3 › reason": the bot counts list entries from zero,
// and an expert reading "trips 2" looks at the second trip instead of the third.
const formatFormbotKey = (key: string): string =>
  key
    .split(".")
    .map((part) => (/^\d+$/.test(part) ? `#${Number(part) + 1}` : part.replace(/_/g, " ")))
    .join(" › ");

const formatFormbotGap = (gap: FormbotGap): string => {
  if (gap.question) return gap.section ? `${gap.section}: ${gap.question}` : gap.question;

  return formatFormbotKey(gap.key || gap.element || "");
};

const formatFormbotNote = (note: FormbotNote): string => {
  const label = note.key ? formatFormbotKey(note.key) : (note.source || "").replace(/_/g, " ");

  if (label && note.why) return `${label}: ${note.why}`;

  return note.why || label;
};

// "ukvi.criminality.espionage" -> "criminality › espionage": the page id is the
// bot's, but it names the part of the application well enough to read.
const formatFormbotPage = (page?: string): string =>
  (page || "")
    .replace(/^(ukvi|sl|sms)\./, "")
    .split(".")
    .map((part) => part.replace(/-/g, " "))
    .join(" › ");

const FORMBOT_LAST_RUN_DATE = "d MMM yyyy, HH:mm";

const FORMBOT_CONFLICT_STATUS = 409;
const FORMBOT_PAGE_READY_EVENT = "formbot:page-ready";

type HoAgentCredentials = {
  user_id: string;
  password: string;
  visa_category: string;
};

type BotCosPlusSwInsideFormType = {
  [key: string]: any;
  document_number: string;
  dob: string;
};

type BotOnlineApplicationCosSwInsideFormType = {
  cos_category: string;
  login: string;
  password: string;
};

@Component({
  components: {
    ServiceTemplate: () => import("@/modules/Services/Components/Template/ServiceTemplate.vue"),
    CreateTaskPopUp: () => import("@/components/Popups/CreateTaskPopUp.vue"),
    MoveToStepPopUp: () => import("@/components/Popups/MoveToStepPopUp.vue"),
    ApproveHOPopUp: () => import("@/components/Popups/ApproveHOPopUp.vue"),
    RejectHOPopUp: () => import("@/components/Popups/RejectHOPopUp.vue"),
    ActionButtons: () => import("@/components/ActionContainer/ActionButtons.vue"),
    PricingRequestsPopups: () => import("@/modules/Pricing/Requests/PricingRequestsPopups.vue"),
  },
})
export default class ServicesTemplate extends Vue {
  loading = false;

  askPopupType = ASK_POPUP_TYPE;
  warningPopupType = WARNING_POPUP_TYPE;

  botPopup = false;
  botPopupWarning = false;

  openActionBotPopup() {
    this.botPopup = true;
  }

  closeActionBotPopup() {
    this.botPopup = false;
  }

  moveToStepPopup = false;

  get isExpert() {
    return SMUser.isExpert;
  }

  openMoveToStepPopup() {
    this.moveToStepPopup = true;

    const target = this.$refs.moveToStepPopup as Vue & { open: () => {} };

    if (!target) return;

    target.open();
  }

  closeMoveToStepPopup() {
    this.moveToStepPopup = false;
  }

  createTaskPopup = false;

  openCreateTaskPopup() {
    this.createTaskPopup = true;

    const target = this.$refs.createTaskPopup as Vue & { open: () => {} };

    if (!target) return;

    target.open();
  }

  closeCreateTaskPopup() {
    this.createTaskPopup = false;
  }

  get containerWithMovingStep() {
    return this.isActionHODecision && this.isCandidateAssessment && this.isExpert;
  }

  get containerWithoutMovingStep() {
    return this.isActionHODecision && !this.isCandidateAssessment && this.isExpert;
  }

  get isActionCreateTask() {
    return (
      this.isActionShown &&
      !this.isCaseStepVisaDecision &&
      !this.isFinalApplicationTab &&
      this.isExpert
    );
  }

  get isActionHODecision() {
    return this.isActionShown && this.isCaseStepVisaDecision;
  }

  get isActionBot() {
    return this.isOnlineApplicationTab && !this.isCaseStepVisaDecision && this.isExpert;
  }

  get okLabel() {
    return this.isApprovedByHo ? WITHDRAW_APPROVAL_LABEL : APPROVED_BY_HO_LABEL;
  }

  get rejectLabel() {
    return this.isRejectedByHo ? WITHDRAW_REJECTION_LABEL : REJECTED_BY_HO_LABEL;
  }

  get isCandidateAssessment() {
    return SMCases.caseType === CANDIDATE_ASSESSMENT_SERVICE_TYPE;
  }

  get isSkilledWorker() {
    return SMCases.caseType === SKILLED_WORKER_SERVICE_TYPE;
  }

  get isSponsorLicense() {
    return SMCases.caseType === SPONSOR_LICENCE_SERVICE_TYPE;
  }

  get isCosPlusSw() {
    return SMCases.caseType === SKILLED_WORKER_COS_SERVICE_TYPE;
  }

  get isCosPlusSwInside() {
    return SMCases.caseType === SKILLED_WORKER_COS_INSIDE_SERVICE_TYPE;
  }

  get isBotAvailable() {
    return this.isSkilledWorker || this.isCosPlusSw;
  }

  get isCosTab(): boolean {
    return this.$router.currentRoute?.query.name === TAB_NAMES[COS_TAB_NAME];
  }

  // ho-agent is retired (EC2 stopped, repo archived, DNS removed 30.10.2026): the form would post the
  // Home Office login and password to a host we no longer own. Hidden until a supported service replaces it.
  get isCosSwInsideBotAvailable(): boolean {
    return false;
  }

  get isApprovedByHo() {
    return SMCases.onlineApplicationStatus === APPROVED_BY_HO_OA_STATUS;
  }

  get isRejectedByHo() {
    return SMCases.onlineApplicationStatus === REJECTED_BY_HO_OA_STATUS;
  }

  get serviceId() {
    return Number(this.$router.currentRoute?.params.id ?? 0);
  }

  get tabTypeId() {
    return this.$router.currentRoute?.params.tabTypeId;
  }

  get isActionShown() {
    return this.$router.currentRoute?.meta?.label !== ROUTE_LABEL_DOCUMENT;
  }

  get isOnlineApplicationTab() {
    return this.$router.currentRoute?.meta?.tabName === ROUTE_NAME_OA;
  }

  get isFinalApplicationTab() {
    return this.$router.currentRoute?.meta?.tabName === ROUTE_NAME_OA;
  }

  get isCaseStepVisaDecision() {
    return (
      SMCases.isLastStep ||
      SMCases.onlineApplicationStatus === APPROVED_BY_HO_OA_STATUS ||
      SMCases.onlineApplicationStatus === REJECTED_BY_HO_OA_STATUS
    );
  }

  get onlineApplicationStatus() {
    return SMCases.onlineApplicationStatus;
  }

  atUpdate() {
    SMCases.setWidgetNeedsUpdate(true);
  }

  async atApprove() {
    if (this.isApprovedByHo) return await this.withdraw();

    this.openApproveConfirmPopup();
  }

  get initApproveConfirmPopup() {
    return this.isExpert;
  }

  approveConfirmPopup = false;

  openApproveConfirmPopup() {
    this.approveConfirmPopup = false;

    this.$nextTick(() => {
      this.approveConfirmPopup = true;
    });
  }

  closeApproveConfirmPopup() {
    this.approveConfirmPopup = false;
  }

  async atReject() {
    if (this.isRejectedByHo) return await this.withdraw();

    this.openRejectConfirmPopup();
  }

  get initRejectConfirmPopup() {
    return this.isExpert;
  }

  rejectConfirmPopup = false;

  openRejectConfirmPopup() {
    this.rejectConfirmPopup = false;

    this.$nextTick(() => {
      this.rejectConfirmPopup = true;
    });
  }

  closeRejectConfirmPopup() {
    this.rejectConfirmPopup = false;
  }

  async withdraw(status = SMCases.onlineApplicationInitialStatus) {
    await this.axios.put(API_ROUTES.CASE_EXPERT_HO_RESET(this.serviceId));

    const newStatus = ![APPROVED_BY_HO_OA_STATUS, REJECTED_BY_HO_OA_STATUS].includes(status)
      ? status
      : "";

    SMCases.setOnlineApplicationStatus(newStatus);
  }

  botCosPlusSwInsideForm: BotCosPlusSwInsideFormType = {
    document_number: "",
    dob: "",
  };

  async transferDataToHO() {
    if (this.loading) return;

    this.botPopup = false;
    this.loading = true;

    let params = {};

    if (this.isCosPlusSwInside) {
      params = { ...this.botCosPlusSwInsideForm };

      const someIsEmpty = Object.keys(params).some(
        (key) => this.botCosPlusSwInsideForm[key] === ""
      );

      if (someIsEmpty) {
        alert(this.$tc("bot.form.someISEmptyAlert"));

        this.loading = false;

        return false;
      }
    }

    try {
      await this.axios({
        method: GET_REQUEST_TYPE,
        url: API_ROUTES.CASE_EXPERT_BOT(this.serviceId, this.tabTypeId),
        timeout: BOT_HTTP_REQUEST_TIMEOUT,
        params,
      });

      SMAlert.setAlertSuccess(true);
    } catch (error) {
      this.botPopupWarning = true;
    } finally {
      this.loading = false;
    }
  }

  cosCategories = COS_CATEGORIES.map((value) => ({
    label: value,
    value,
  }));

  // CoS SW Inside Bot

  cosSwInsideBotPopup = false;

  openCosSwInsideBotPopup() {
    this.cosSwInsideBotPopup = true;
  }

  closeCosSwInsideBotPopup() {
    this.cosSwInsideBotPopup = false;
  }

  botCosSwInsideForm: BotOnlineApplicationCosSwInsideFormType = {
    cos_category: "",
    login: "",
    password: "",
  };

  get isBotCosSwInsideFormDisabled() {
    return (
      !this.botCosSwInsideForm ||
      !this.botCosSwInsideForm.cos_category ||
      !this.botCosSwInsideForm.login ||
      !this.botCosSwInsideForm.password
    );
  }

  async assertHoAgentSessionAvailable(): Promise<boolean> {
    try {
      await this.axios.get(`${HO_AGENT_API_BASE}/case/${this.serviceId}/session`);
      alert("Error: Session already exists for this case");

      return false;
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;

      if (status !== 404) throw error;

      return true;
    }
  }

  async transferToHoAgent(bot: HoAgentBotType, credentials?: HoAgentCredentials) {
    const sessionAvailable = await this.assertHoAgentSessionAvailable();

    if (!sessionAvailable) return;

    const { data } = await this.axios.get(
      API_ROUTES.CASE_ONLINE_APPLICATION_STRUCTURED_DATA(this.serviceId, bot)
    );

    await this.axios.post(
      `${HO_AGENT_API_BASE}/fill-form`,
      {
        case_id: String(this.serviceId),
        type: bot,
        ...(credentials ?? {}),
        data,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    SMAlert.setAlertSuccess(true);
  }

  async transferDataToHOCosSwInside() {
    if (!this.serviceId || this.isBotCosSwInsideFormDisabled) return;

    try {
      this.loading = true;

      await this.transferToHoAgent("cos", {
        user_id: this.botCosSwInsideForm.login,
        password: this.botCosSwInsideForm.password,
        visa_category: this.botCosSwInsideForm.cos_category,
      });
    } catch (error) {
      this.botPopupWarning = true;
    } finally {
      this.loading = false;

      this.closeCosSwInsideBotPopup();
    }
  }

  closeAlert() {
    SMAlert.setAlertSuccess(false);
    SMAlert.setAlertError(false);
  }

  get documentIsApproved() {
    return SMDocuments.documentCard?.expert_status === APPROVED_SERVICE_STATUS;
  }

  get isDocumentApproveDisabled() {
    return (
      SMDocuments.documentCard?.status === NOT_PROVIDED_DOCUMENT_STATUS ||
      SMDocuments.documentCard?.status === ERROR_DOCUMENT_STATUS
    );
  }

  get textButton() {
    return this.documentIsApproved
      ? this.$tc("labels.document.withdrawApproval")
      : this.$tc("labels.document.approveDocument");
  }

  get isExpertDocumentTemplate() {
    return SMUser.isExpert && !this.isActionShown;
  }

  async expertUpdateDocumentStatus() {
    const { document_id, id } = this.$router.currentRoute?.params;

    const status = this.documentIsApproved ? TO_REVIEW_SERVICE_STATUS : APPROVED_SERVICE_STATUS;

    if (!document_id || !id) return;

    await this.axios.get(API_ROUTES.CASE_EXPERT_DOCUMENT_STATUS(id, document_id, status));

    await this.initDocument();
  }

  async initDocument() {
    const { document_id, id } = this.$router.currentRoute?.params;

    SMDocuments.setDocumentId(document_id);
    SMDocuments.setCaseId(Number(id ?? 0));

    const data = await SMDocuments.fetchDocumentCard();

    if (!data) return;

    SMDocuments.setDocumentCard(data);
    SMDocuments.initChecks();
    SMDocuments.initSelectedChecks();
  }

  get caseTemplateId() {
    return SMCases.caseTemplateId;
  }

  @Watch("caseTemplateId")
  updateTabId(value: number) {
    if (value && this.serviceId) this.getCaseProgressSteps();
  }

  @Watch("serviceId")
  onFormbotServiceIdChange() {
    this.resetFormbotState();
    this.prepareFormbotPlan();
  }

  @Watch("isRunBotAvailable", { immediate: true })
  onFormbotAvailability() {
    this.prepareFormbotPlan();
  }

  progressSteps: TTaskSelect[] = [];

  async getCaseProgressSteps() {
    if (!SMUser.isExpert) return;

    const { data } = await this.axios.get<API_TTaskSelect[]>(
      API_ROUTES.CASE_EXPERT_CASE_PROGRESS_STEP(this.serviceId, SMCases.caseTemplateId)
    );

    if (!data) return;

    this.progressSteps = data.map((item) => ({ value: item.ID, label: item.name }));
  }

  created() {
    this.$root.$on(FORMBOT_PAGE_READY_EVENT, this.onFormbotPageReady);
  }

  mounted() {
    if (this.serviceId) this.getCaseProgressSteps();

    if (SMUser.isEmployee && !this.dependantsList?.length) {
      SMAdditionalInfo.fetchDependantsList();
    }
  }

  get isEmployee() {
    return SMUser.isEmployee;
  }

  get dependantsList() {
    return SMAdditionalInfo.dependantsList;
  }

  get isSwInside() {
    return (
      SMCases.caseType === SKILLED_WORKER_COS_INSIDE_SERVICE_TYPE ||
      SMCases.caseType === SKILLED_WORKER_INSIDE_SERVICE_TYPE
    );
  }

  get isRunBotAvailable() {
    return (
      (this.isSwInside || this.isSponsorLicense) &&
      this.isExpert &&
      (this.isOnlineApplicationTab || this.isFinalApplicationTab)
    );
  }

  loadingFormbotPlan = false;
  loadingFormbotRun = false;
  formbotHasStarted = false;
  formbotAlreadyInProgress = false;
  formbotStartedNotify = "";
  formbotError = "";
  formbotMissing: FormbotGap[] = [];
  formbotUnsupported: FormbotGap[] = [];
  formbotWarnings: FormbotGap[] = [];
  formbotNotes: FormbotNote[] = [];
  formbotLastRun: FormbotLastRun | null = null;
  formbotRequestId = 0;
  formbotInFlightId = 0;

  get formbotMissingLabels(): string[] {
    // formbot can name one key twice (once per page that asks it): one line each.
    return Array.from(new Set(this.formbotMissing.map(formatFormbotGap).filter(Boolean)));
  }

  get formbotMissingText(): string {
    if (!this.formbotMissingLabels.length) return "";

    return this.$tc("formbot.missing");
  }

  get formbotUnsupportedLabels(): string {
    return this.formbotUnsupported
      .map(formatFormbotGap)
      .filter(Boolean)
      .join(", ");
  }

  get formbotUnsupportedText(): string {
    if (!this.formbotUnsupportedLabels) return "";

    return String(this.$t("formbot.unsupported", { fields: this.formbotUnsupportedLabels }));
  }

  get formbotWarningLines(): string[] {
    return this.formbotWarnings
      .map((gap) => {
        const field = formatFormbotGap(gap);

        if (!field) return "";

        // An unanswered question gov.uk asks every fresh application (espionage)
        // arrives as a warning too, and "a format the form does not accept" would
        // send the expert looking for a typo in an answer that is not there.
        const key = gap.because === "missing" ? "formbot.warningMissing" : "formbot.warning";
        const text = String(this.$t(key, { field }));
        const detail = gap.detail?.trim();

        return detail ? `${text} ${detail}` : text;
      })
      .filter(Boolean);
  }

  get formbotNotesText(): string {
    const notes = this.formbotNotes
      .map(formatFormbotNote)
      .filter(Boolean)
      .join("; ");

    if (!notes) return "";

    return String(this.$t("formbot.notes", { notes }));
  }

  get formbotSponsorAccountHint(): string {
    if (!this.isSponsorLicense || !this.isRunBotAvailable) return "";

    return this.$tc("formbot.sponsorAccount");
  }

  get formbotSponsorSessionHint(): string {
    if (!this.isSponsorLicense || !this.isRunBotAvailable) return "";

    return this.$tc("formbot.sponsorSession");
  }

  get isFormbotRunning(): boolean {
    return this.formbotLastRun?.status === "running";
  }

  // The previous run is not news once this page has started a new one.
  get formbotShowsLastRun(): boolean {
    return (
      Boolean(this.formbotLastRun) && (!this.formbotHasStarted || this.formbotAlreadyInProgress)
    );
  }

  get formbotLastRunText(): string {
    const run = this.formbotLastRun;

    if (!run || !this.formbotShowsLastRun) return "";

    const started = new Date(run.started_at);
    const when = isNaN(started.getTime()) ? "" : format(started, FORMBOT_LAST_RUN_DATE);
    const who = run.expert || String(this.$t("formbot.lastRunSomeone"));

    if (run.status === "running") return String(this.$t("formbot.lastRunRunning", { when, who }));

    const outcome = String(this.$t(`formbot.outcome.${run.status}`));
    const page = run.status !== "done" ? formatFormbotPage(run.page) : "";
    const where = page ? String(this.$t("formbot.lastRunWhere", { page })) : "";

    return String(this.$t("formbot.lastRun", { when, who, outcome, where }));
  }

  get formbotLastRunExplanation(): string {
    if (!this.formbotShowsLastRun || this.isFormbotRunning) return "";

    return this.formbotLastRun?.explanation || "";
  }

  get formbotLastRunGaps(): string[] {
    if (!this.formbotLastRunExplanation) return [];

    return this.formbotLastRun?.gaps ?? [];
  }

  get formbotSameInputText(): string {
    if (!this.formbotShowsLastRun || !this.formbotLastRun?.same_input) return "";

    return this.$tc("formbot.sameInput");
  }

  get formbotStartedText(): string {
    // The last-run line already says who started the run going now, and when.
    if (this.formbotAlreadyInProgress && this.isFormbotRunning) return "";

    if (this.formbotAlreadyInProgress) return this.$tc("formbot.alreadyInProgress");

    if (!this.formbotHasStarted) return "";

    return this.formbotStartedNotify
      ? String(this.$t("formbot.started", { email: this.formbotStartedNotify }))
      : this.$tc("formbot.startedGeneric");
  }

  get formbotHasHints(): boolean {
    return Boolean(
      this.formbotStartedText ||
        this.formbotLastRunText ||
        this.formbotMissingText ||
        this.formbotUnsupportedText ||
        this.formbotWarningLines.length ||
        this.formbotNotesText ||
        this.formbotSponsorAccountHint ||
        this.formbotSponsorSessionHint ||
        this.formbotError
    );
  }

  get isFormbotRunDisabled(): boolean {
    return (
      this.loadingFormbotPlan ||
      this.loadingFormbotRun ||
      this.formbotHasStarted ||
      this.formbotAlreadyInProgress ||
      this.isFormbotRunning ||
      this.formbotMissing.length > 0 ||
      this.formbotUnsupported.length > 0
    );
  }

  clearFormbotGaps() {
    this.formbotMissing = [];
    this.formbotUnsupported = [];
    this.formbotWarnings = [];
    this.formbotNotes = [];
  }

  prepareFormbotPlan() {
    if (!this.isRunBotAvailable || this.formbotHasStarted || this.formbotAlreadyInProgress) return;

    this.loadingFormbotPlan = true;
    this.formbotError = "";
    this.clearFormbotGaps();
  }

  onFormbotPageReady() {
    this.fetchFormbotPlan();
  }

  resetFormbotState() {
    this.formbotRequestId += 1;
    this.formbotInFlightId = 0;
    this.loadingFormbotPlan = false;
    this.loadingFormbotRun = false;
    this.formbotHasStarted = false;
    this.formbotAlreadyInProgress = false;
    this.formbotStartedNotify = "";
    this.formbotError = "";
    this.formbotLastRun = null;
    this.clearFormbotGaps();
  }

  applyFormbotPlan(data?: FormbotPlanResponse) {
    this.formbotLastRun = data?.last_run ?? null;
    this.formbotMissing = [
      ...asFormbotList(data?.plan?.missing),
      ...asFormbotList(data?.plan?.malformed),
    ];
    this.formbotUnsupported = asFormbotList(data?.plan?.unsupported);
    this.formbotWarnings = asFormbotList(data?.warnings);
    this.formbotNotes = asFormbotNoteList(data?.notes);
  }

  async fetchFormbotPlan() {
    if (!this.serviceId || !this.isRunBotAvailable || this.formbotHasStarted) return;
    if (this.formbotAlreadyInProgress || this.formbotInFlightId) return;

    const requestId = ++this.formbotRequestId;

    this.formbotInFlightId = requestId;
    this.loadingFormbotPlan = true;
    this.formbotError = "";
    this.clearFormbotGaps();

    try {
      const { data } = await this.axios.get<FormbotPlanResponse>(
        API_ROUTES.CASE_FORMBOT_PLAN(this.serviceId)
      );

      if (requestId !== this.formbotRequestId) return;

      this.applyFormbotPlan(data);
    } catch (error) {
      if (requestId !== this.formbotRequestId) return;

      this.formbotError = this.getFormbotErrorMessage(error);
    } finally {
      if (this.formbotInFlightId === requestId) this.formbotInFlightId = 0;
      if (requestId === this.formbotRequestId) this.loadingFormbotPlan = false;
    }
  }

  async runFormbot() {
    if (!this.serviceId || this.isFormbotRunDisabled) return;

    this.loadingFormbotRun = true;
    this.formbotError = "";

    try {
      const { data } = await this.axios.post<FormbotRunResponse>(
        API_ROUTES.CASE_FORMBOT_RUN(this.serviceId)
      );

      this.formbotHasStarted = true;
      this.formbotAlreadyInProgress = false;
      this.formbotStartedNotify = data?.notify || "";
      this.clearFormbotGaps();
      this.formbotWarnings = asFormbotList(data?.warnings);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      const data = (error as { response?: { data?: FormbotRunResponse } })?.response?.data;

      if (status === FORMBOT_CONFLICT_STATUS) {
        this.formbotWarnings = asFormbotList(data?.warnings);

        if (Array.isArray(data?.missing)) {
          this.formbotMissing = asFormbotList(data.missing);
          this.formbotNotes = asFormbotNoteList(data?.notes);
          return;
        }

        this.formbotHasStarted = true;
        this.formbotAlreadyInProgress = true;
        this.formbotStartedNotify = "";
        this.formbotLastRun = data?.last_run ?? this.formbotLastRun;
        return;
      }

      this.formbotError = this.getFormbotErrorMessage(error);
    } finally {
      this.loadingFormbotRun = false;
    }
  }

  getFormbotErrorMessage(error: unknown): string {
    const data = (error as { response?: { data?: FormbotRunResponse | string } })?.response?.data;

    if (data && typeof data === "object" && typeof data.message === "string") {
      return data.message;
    }

    return this.$tc("formbot.error");
  }

  beforeDestroy() {
    this.$root.$off(FORMBOT_PAGE_READY_EVENT, this.onFormbotPageReady);
    this.formbotRequestId += 1;
  }
}
